/**
 * Pending seals (REWRITE_PLAN.md §9 — offline-first is the crux).
 *
 * When a letter is sealed while offline — or the network dies mid-seal — the
 * request is parked here and flushed when connectivity returns. Sealing needs
 * no client-generated id (the server owns it), so the same request is safe to
 * retry; the queue only removes an entry after a definitive 2xx.
 *
 * IndexedDB, not localStorage, for the same reason as drafts: a long letter
 * must survive a tab close, and it must never synchronise anywhere.
 */

import type { PostageClass } from '@toiyp/shared';

const DB_NAME = 'toiyp-seal-queue';
const STORE = 'pending';

export interface PendingSeal {
  id: string;
  relationshipId: string;
  body: string;
  queuedAt: number;
  /**
   * The stamp chosen when it was sealed. Only a request — the server checks it
   * again on replay, and a class lost in the meantime goes 2nd class.
   */
  postage?: PostageClass;
  /** Stamp artwork chosen (custom stamps) — checked again on replay. */
  stampDesignId?: string;
  /** Letter paper chosen (stationery) — checked again on replay. */
  stationeryId?: string;
  /** Envelope chosen — checked again on replay. */
  envelopeId?: string;
  /**
   * Time-to-write, frozen when it was queued: the draft is forgotten the
   * moment it goes offline, so the number has to be kept here to survive the
   * wait. Banded by the server before anyone sees it.
   */
  composeMs?: number;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const sealQueue = {
  all: () => tx<PendingSeal[]>('readonly', (s) => s.getAll() as IDBRequest<PendingSeal[]>),
  put: (seal: PendingSeal) => tx<IDBValidKey>('readwrite', (s) => s.put(seal)),
  remove: (id: string) => tx<undefined>('readwrite', (s) => s.delete(id) as IDBRequest<undefined>),
  clear: () => tx<undefined>('readwrite', (s) => s.clear() as IDBRequest<undefined>),
};